import { DOMParser } from 'xmldom';
import xpath from 'xpath';
import type { Position } from 'geojson';
import { Mission, Zone } from '../db';
import { processMultipolygonCoords } from './BoundaryService';
import { XAG_PLACEMARK_TYPE, VALID_XAG_PLACEMARK_TYPES, XAG_PLACEMARK_TYPE_TO_ZONE_TYPE } from './KmlToXagConverter';
import { ZONE_TYPE_TO_KML_FEATURE_TYPE } from '../db/ZoneTypesDatatype';

function getPlacemarkType(placemark: Element): XAG_PLACEMARK_TYPE | null {
  const select = xpath.useNamespaces({ kml: 'http://www.opengis.net/kml/2.2' });

  // 1. Try ExtendedData > SchemaData > SimpleData name="type"
  const typeNodeElements = select('.//kml:ExtendedData/kml:SchemaData/kml:SimpleData[@name="type"]', placemark);

  if (!typeNodeElements) {
    return null;
  }

  const typeNode = typeNodeElements[0] as Element | undefined;

  if (typeNode) {
    const value = typeNode.textContent?.trim().toLowerCase();

    if (value && VALID_XAG_PLACEMARK_TYPES.includes(value as XAG_PLACEMARK_TYPE)) {
      return value as XAG_PLACEMARK_TYPE;
    }
  }

  // 2. Fallback to name
  const nameNode = select('string(kml:name)', placemark) as string;
  const name = nameNode?.trim().toLowerCase();

  if (name) {
    for (const t of VALID_XAG_PLACEMARK_TYPES) {
      if (name.includes(t)) {
        return t;
      }
    }
  }

  return null;
}

export async function importPolygonsFromXagFolder(kmlText: string, mission: Mission) {
  const inputDoc = new DOMParser().parseFromString(kmlText, 'text/xml');
  const select = xpath.useNamespaces({ kml: 'http://www.opengis.net/kml/2.2' });

  // Search for job_id in the kml::name
  let folderElements = select(`//kml:Document/kml:Folder[contains(kml:name, "${mission.job_id}")]`, inputDoc);

  if (!Array.isArray(folderElements) || folderElements.length === 0) {
    // Try mission.name instead (for backwards compatibility)
    folderElements = select(`//kml:Document/kml:Folder[kml:name="${mission.name}"]`, inputDoc);

    if (!Array.isArray(folderElements) || folderElements.length === 0) {
      throw new Error(`Folder "${mission.name}" and "${mission.job_id}" not found in KML (no elements).`);
    }
  }

  const folder = folderElements[0] as Element | undefined;

  if (!folder) {
    throw new Error(`Folder "${mission.name}" and "${mission.job_id}" not found in KML.`);
  }

  const placemarks = select('kml:Placemark', folder) as Element[];

  for (const placemark of placemarks) {
    const type = getPlacemarkType(placemark);

    if (!type) {
      console.warn('Skipping placemark with unknown type');

      continue;
    }

    const polygons = select('.//kml:Polygon', placemark) as Element[];

    const multiPolygonCoords: Position[][][] = [];

    for (const polygon of polygons) {
      const outer = select('.//kml:outerBoundaryIs/kml:LinearRing/kml:coordinates', polygon) as Element[];
      const inners = select('.//kml:innerBoundaryIs/kml:LinearRing/kml:coordinates', polygon) as Element[];

      const parseCoords = (el: Element): Position[] => {
        return (
          el.textContent
            ?.trim()
            .split(/\s+/)
            .map((coord) => {
              const [lon, lat] = coord.split(',').map(parseFloat);

              return [lon, lat] as Position;
            }) ?? []
        );
      };

      const polygonCoords: Position[][] = [];

      if (outer.length) {
        polygonCoords.push(parseCoords(outer[0]));
      }

      for (const inner of inners) {
        polygonCoords.push(parseCoords(inner));
      }

      multiPolygonCoords.push(polygonCoords);
    }

    if (multiPolygonCoords.length > 0) {
      const newZoneType = XAG_PLACEMARK_TYPE_TO_ZONE_TYPE[type];
      let zone = mission.getZones().find((z) => z.zone_type === newZoneType);

      if (!zone) {
        [zone] = await Zone.new_zone(ZONE_TYPE_TO_KML_FEATURE_TYPE[newZoneType], [], mission.instance_id);
      }

      if (!zone) {
        throw new Error(`Could not create or load a zone.`);
      }

      await processMultipolygonCoords(mission, multiPolygonCoords, zone);
    }
  }
}
